<template>
	<div class="ht">
		<p class="ht-intro">
			Everything the pipeline has recorded for you<template v-if="history && history.items.length">, newest first (latest {{ history.items.length }})</template>.
		</p>

		<p v-if="error" class="ht-note ht-note--error">{{ error }}</p>
		<p v-else-if="!history" class="ht-note">{{ loading ? "Loading your history…" : "History loads when you open this tab." }}</p>
		<p v-else-if="history.available === false" class="ht-note ht-note--error">History could not load this time. Refresh to try again.</p>
		<p v-else-if="!history.items.length" class="ht-note">Nothing recorded yet. Browse jobs, courses or assessments.</p>

		<template v-else>
			<section class="ht-overview">
				<button type="button" class="ht-overview__toggle" :aria-expanded="String(overviewOpen)" aria-controls="ht-overview-body" @click="toggleOverview">
					<span>Summary</span>
					<span class="ht-overview__hint">{{ overviewOpen ? "Hide" : "Show" }}</span>
					<svg :class="{ 'ht-overview__chevron--open': overviewOpen }" class="ht-overview__chevron" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true"><path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" /></svg>
				</button>
				<div v-show="overviewOpen" id="ht-overview-body" class="ht-overview__body">
					<h3>What you did</h3>
					<div class="ht-summary">
						<span v-for="item in summary" :key="item.key" class="ht-stat" :style="{ '--area': item.color }">
							<b>{{ item.count }}</b> {{ item.label }}
						</span>
					</div>
					<template v-if="totals">
						<h3>Total gained</h3>
						<p class="ht-overview__what">{{ totals.what }}</p>
						<p class="ht-points">
							<span v-for="point in totals.points" :key="point.key" class="ht-point" :class="{ 'ht-point--none': point.area === 'activity' }">{{ pointLabel(point) }}</span>
						</p>
					</template>
				</div>
			</section>

			<nav class="ht-filters" aria-label="Filter history">
				<button
					v-for="f in filters"
					:key="f.key"
					type="button"
					class="ht-filter"
					:class="{ 'ht-filter--active': filter === f.key }"
					@click="filter = f.key"
				>{{ f.label }}</button>
			</nav>

			<section v-if="filter === 'topcard'" class="ht-cards" aria-label="Top card clicks">
				<h3>Top card order</h3>
				<p class="ht-cards__what">Most-clicked first. Equal counts keep the default order.</p>
				<ol>
					<li v-for="(card, index) in cardRanking" :key="card.key">
						<span class="ht-cards__rank">{{ index + 1 }}</span>
						<span class="ht-cards__name">{{ card.name }}</span>
						<span class="ht-cards__bar"><i :style="{ width: card.share + '%' }" /></span>
						<b>{{ card.clicks }}</b>
					</li>
				</ol>
			</section>

			<p v-if="!days.length" class="ht-note">Nothing of this kind in your recent history.</p>
			<section v-for="day in days" :key="day.key" class="ht-day">
				<h3>{{ day.label }}</h3>
				<ol class="ht-list">
					<li v-for="row in day.rows" :key="row.id" class="ht-row" :style="{ '--area': row.color }">
						<time>{{ row.time }}</time>
						<div class="ht-body">
							<p class="ht-what">
								{{ sentenceStart(row) }}
								<strong v-if="row.subject">{{ row.subject }}</strong>
								<span v-if="row.repeat > 1" class="ht-repeat">{{ repeatLabel(row) }}</span>
							</p>
							<p v-if="row.itemType" class="ht-tags">
								<span v-if="row.area" class="ht-tag">{{ row.area }}</span>
								<span v-else class="ht-tag ht-tag--none" title="This item has no area tag yet, so it counts as activity but not towards career direction">No area tag</span>
								<span v-if="!row.processed" class="ht-tag ht-tag--pending">Processing</span>
							</p>
							<p v-if="row.processed && row.points" class="ht-points" aria-label="Score given">
								<span
									v-for="point in row.points"
									:key="point.key"
									class="ht-point"
									:class="{ 'ht-point--none': point.area === 'activity' }"
									:title="point.area === 'activity' ? ACTIVITY_NOTE : null"
								>{{ pointLabel(point) }}</span>
							</p>
						</div>
					</li>
				</ol>
			</section>
		</template>
	</div>
</template>

<script>
import { AREA_COLORS, TOP_CARD_NAMES, cardName, tagName } from "./hudFormat";

// Each event as a short sentence, and the area colour it belongs to.
const EVENTS = {
	"job.viewed": { verb: "Viewed job", kind: "job" },
	"job.apply_intent": { verb: "Started applying to", kind: "job" },
	"job.applied": { verb: "Applied to", kind: "job" },
	"course.viewed": { verb: "Viewed course", kind: "course" },
	"course.purchase_intent": { verb: "Started buying course", kind: "course" },
	"course.enroll_intent": { verb: "Started enrolling in", kind: "course" },
	"course.purchased": { verb: "Bought course", kind: "course" },
	"assessment.viewed": { verb: "Viewed assessment", kind: "assessment" },
	"assessment.opened": { verb: "Started assessment", kind: "assessment" },
	"assessment.purchased": { verb: "Bought assessment", kind: "assessment" },
	"section.viewed": { verb: "Saw the dashboard section", kind: "dashboard" },
	"section.dwell": { verb: "Spent time on", kind: "dashboard" },
	"topcard.selected": { verb: "Clicked the top card", kind: "topcard" },
};
const KIND_COLORS = {
	job: AREA_COLORS.career,
	course: AREA_COLORS.momentum,
	assessment: AREA_COLORS.assessment,
	dashboard: AREA_COLORS.sections,
	topcard: AREA_COLORS.recent,
	other: AREA_COLORS.activity,
};
const KIND_LABELS = {
	job: ["job view", "job views"],
	course: ["course view", "course views"],
	assessment: ["assessment action", "assessment actions"],
	dashboard: ["dashboard action", "dashboard actions"],
	topcard: ["top card click", "top card clicks"],
	other: ["other action", "other actions"],
};

export default {
	name: "HudHistoryTab",
	props: {
		history: { type: Object, default: null },
		loading: { type: Boolean, default: false },
		error: { type: String, default: null },
		// Lifetime clicks per top card from the profile: the numbers that set the order.
		topCardClicks: { type: Object, default: null },
	},
	data() {
		return {
			ACTIVITY_NOTE,
			overviewOpen: readOverviewOpen(),
			filter: "all",
			filters: [
				{ key: "all", label: "All" },
				{ key: "course", label: "Courses" },
				{ key: "job", label: "Jobs" },
				{ key: "assessment", label: "Assessments" },
				{ key: "dashboard", label: "Dashboard" },
				{ key: "topcard", label: "Top cards" },
			],
		};
	},
	computed: {
		// Same rule as the pipeline: most clicks first, ties keep the default order.
		cardRanking() {
			const counts = this.topCardClicks || {};
			const clicks = (key) => (Number.isFinite(counts[key]) && counts[key] > 0 ? counts[key] : 0);
			const keys = Object.keys(TOP_CARD_NAMES);
			const most = Math.max(1, ...keys.map(clicks));
			return keys
				.map((key, index) => ({ key, index, name: cardName(key), clicks: clicks(key) }))
				.sort((a, b) => b.clicks - a.clicks || a.index - b.index)
				.map((card) => ({ ...card, share: Math.round((card.clicks / most) * 100) }));
		},
		items() {
			return ((this.history && this.history.items) || []).map((item) => {
				const meta = EVENTS[item.name] || { verb: item.name, kind: "other" };
				return { ...item, kind: meta.kind, verb: meta.verb, color: KIND_COLORS[meta.kind] };
			});
		},
		summary() {
			const counts = {};
			this.items.forEach((item) => {
				counts[item.kind] = (counts[item.kind] || 0) + 1;
			});
			return Object.keys(counts)
				.map((kind) => {
					const [one, many] = KIND_LABELS[kind];
					return { key: kind, count: counts[kind], label: counts[kind] === 1 ? one : many, color: KIND_COLORS[kind] };
				})
				.sort((a, b) => b.count - a.count);
		},
		// Every point the listed events gave, summed per area and tag, under the chosen filter.
		totals() {
			if (this.history && this.history.pointsAvailable === false) return null;
			const shown = this.items.filter((item) => (this.filter === "all" || item.kind === this.filter) && item.processed);
			if (!shown.length) return null;
			let points = null;
			shown.forEach((item) => {
				points = addPoints(points, pointsOf(item) || []);
			});
			points = (points || []).sort((a, b) => AREA_ORDER.indexOf(a.area) - AREA_ORDER.indexOf(b.area) || b.value - a.value);
			const count = shown.length;
			return { points, what: `From the ${count} processed event${count === 1 ? "" : "s"} listed below.` };
		},
		days() {
			const rows = [];
			this.items
				.filter((item) => this.filter === "all" || item.kind === this.filter)
				.forEach((item) => {
					const prev = rows[rows.length - 1];
					// Back-to-back repeats of the same thing read as one line with a count.
					if (prev && prev.name === item.name && prev.itemId === item.itemId && sameSection(prev, item) && prev.dayKey === dayKey(item.at)) {
						prev.repeat += 1;
						prev.dwellMs += dwellOf(item);
						prev.points = addPoints(prev.points, pointsOf(item));
						return;
					}
					rows.push(describe(item));
				});
			const days = [];
			rows.forEach((row) => {
				let day = days[days.length - 1];
				if (!day || day.key !== row.dayKey) {
					day = { key: row.dayKey, label: dayLabel(row.at), rows: [] };
					days.push(day);
				}
				day.rows.push(row);
			});
			return days;
		},
	},
	methods: {
		toggleOverview() {
			this.overviewOpen = !this.overviewOpen;
			try {
				localStorage.setItem(OVERVIEW_KEY, this.overviewOpen ? "open" : "closed");
			} catch (err) {
				// Storage blocked: the toggle still works for this visit.
			}
		},
		// A merged "time spent" line reads as the total, so "9 s ×2" is never mistaken for 9 s in all.
		sentenceStart(row) {
			return row.name === "section.dwell" && row.dwellMs > 0 ? `Spent ${duration(row.dwellMs)} on` : row.verb;
		},
		repeatLabel(row) {
			return row.name === "section.dwell" ? `over ${row.repeat} visits` : `${row.repeat} times in a row`;
		},
		// The score one event (or a merged run of them) added, in tester words.
		pointLabel(point) {
			const value = `+${number(point.value)}`;
			if (point.area === "career" && point.kind === "view") return `${value} ${point.value === 1 ? "view" : "views"} to ${tagName(point.tag)}`;
			if (point.area === "career") return `${value} career points to ${tagName(point.tag)}`;
			if (point.area === "momentum") return `${value} learning momentum`;
			if (point.area === "activity") return point.tag ? `${value} activity on ${point.tag}` : `${value} activity`;
			return `${value} to ${tagName(point.tag)} section`;
		},
	},
};

const AREA_ORDER = ["career", "momentum", "section", "activity"];
const ACTIVITY_NOTE = "Counted as activity where it happened. It adds to your total events, not to any score.";
// The panel's own state, so the summary stays as the tester left it after a refresh.
const OVERVIEW_KEY = "hire3x_insights_history_summary";

// Points as listed, keyed for rendering; null when the pipeline could not read them.
// An event that scored nothing shows as one activity, on the section it happened in when it names one.
function pointsOf(item) {
	if (!Array.isArray(item.points)) return null;
	const points = item.points
		.filter((p) => p && typeof p.value === "number" && isFinite(p.value))
		.map((p) => ({ ...p, key: `${p.area}:${p.kind}:${p.tag || ""}` }));
	if (points.length || !item.processed) return points;
	const place = activityPlace(item);
	return [{ area: "activity", kind: "activity", tag: place, value: 1, key: `activity:${place || ""}` }];
}

function activityPlace(item) {
	const section = (item.props || {}).sectionId;
	return typeof section === "string" && section ? tagName(section.replace(/_panel$/, "")) : null;
}

// Summed per area and tag, so "3 times in a row" shows the run's total, not one event's share.
function addPoints(total, more) {
	if (!total || !more) return total || more;
	const sum = total.map((p) => ({ ...p }));
	more.forEach((p) => {
		const same = sum.find((q) => q.key === p.key);
		if (same) same.value += p.value;
		else sum.push({ ...p });
	});
	return sum;
}

function number(value) {
	return Math.round(value * 10) / 10;
}

function readOverviewOpen() {
	try {
		return localStorage.getItem(OVERVIEW_KEY) !== "closed";
	} catch (err) {
		return true;
	}
}

function dwellOf(item) {
	const ms = Number((item.props || {}).dwellMs);
	return item.name === "section.dwell" && Number.isFinite(ms) && ms > 0 ? ms : 0;
}

function duration(ms) {
	const seconds = Math.max(1, Math.round(ms / 1000));
	if (seconds < 60) return `${seconds} s`;
	const minutes = Math.floor(seconds / 60);
	const rest = seconds % 60;
	return rest ? `${minutes} min ${rest} s` : `${minutes} min`;
}

function describe(item) {
	const props = item.props || {};
	let subject = item.title || item.itemId || "";
	if (item.kind === "topcard") subject = cardName(props.cardId);
	if (item.kind === "dashboard") {
		subject = props.sectionId ? tagName(String(props.sectionId).replace(/_panel$/, "")) : props.cardId || "";
	}
	const area = item.subdomainTag ? `${tagName(item.domainTag)} › ${tagName(item.subdomainTag)}` : item.domainTag ? tagName(item.domainTag) : "";
	return { ...item, subject, area, repeat: 1, dwellMs: dwellOf(item), points: pointsOf(item), dayKey: dayKey(item.at), time: timeOf(item.at) };
}

function sameSection(a, b) {
	return (a.props || {}).sectionId === (b.props || {}).sectionId && (a.props || {}).cardId === (b.props || {}).cardId;
}

function parse(at) {
	// Stored times are UTC; a missing zone is read as UTC, not local.
	const text = typeof at === "string" && !/[zZ]|[+-]\d{2}:?\d{2}$/.test(at) ? `${at}Z` : at;
	const ms = Date.parse(text);
	return isFinite(ms) ? new Date(ms) : null;
}

function dayKey(at) {
	const d = parse(at);
	return d ? d.toDateString() : "unknown";
}

function dayLabel(at) {
	const d = parse(at);
	if (!d) return "Unknown date";
	const today = new Date();
	const yesterday = new Date(Date.now() - 86400000);
	if (d.toDateString() === today.toDateString()) return "Today";
	if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
	return d.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "short" });
}

function timeOf(at) {
	const d = parse(at);
	return d ? d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }) : "";
}
</script>

<style scoped>
.ht {
	padding: 12px 14px 24px;
}
.ht-intro {
	margin: 0 0 10px;
	font-size: 13px;
	line-height: 1.5;
	color: #464667;
}
.ht-filter {
	flex: none;
	padding: 5px 10px;
	border: 1px solid #20639b;
	border-radius: 8px;
	background: #fff;
	color: #20639b;
	font: 600 12px "Montserrat", sans-serif;
	cursor: pointer;
}
.ht-filter:focus-visible {
	outline: 2px solid #4a7eff;
	outline-offset: 2px;
}
.ht-note {
	font-size: 13px;
	color: #72738e;
}
.ht-note--error {
	color: #b3261e;
}

.ht-overview {
	margin-bottom: 12px;
	border: 1px solid #e2e8f0;
	border-radius: 10px;
	background: #f9fbfd;
}
.ht-overview__toggle {
	display: flex;
	align-items: center;
	gap: 8px;
	width: 100%;
	padding: 9px 12px;
	border: none;
	border-radius: 10px;
	background: none;
	color: #1a4164;
	font: 700 13px "Montserrat", sans-serif;
	text-align: left;
	cursor: pointer;
}
.ht-overview__toggle:focus-visible {
	outline: 2px solid #4a7eff;
	outline-offset: 2px;
}
.ht-overview__hint {
	margin-left: auto;
	font: 600 11.5px "Montserrat", sans-serif;
	color: #72738e;
}
.ht-overview__chevron {
	color: #72738e;
	transition: transform 0.15s ease;
}
.ht-overview__chevron--open {
	transform: rotate(180deg);
}
.ht-overview__body {
	padding: 0 12px 12px;
}
.ht-overview__body h3 {
	margin: 10px 0 6px;
	font: 700 12.5px "Montserrat", sans-serif;
	color: #464667;
}
.ht-overview__body h3:first-child {
	margin-top: 0;
}
.ht-overview__what {
	margin: -2px 0 0;
	font-size: 12px;
	color: #72738e;
}
@media (prefers-reduced-motion: reduce) {
	.ht-overview__chevron {
		transition: none;
	}
}
.ht-summary {
	display: flex;
	flex-wrap: wrap;
	gap: 6px;
	margin-bottom: 10px;
}
.ht-stat {
	padding: 5px 10px;
	border-radius: 14px;
	border-left: 3px solid var(--area);
	background: #f6f9fb;
	font-size: 12.5px;
	color: #464667;
}
.ht-stat b {
	font: 700 13px "Montserrat", sans-serif;
	color: var(--area);
}

.ht-filters {
	display: flex;
	flex-wrap: wrap;
	gap: 6px;
	margin-bottom: 12px;
}
.ht-filter {
	border-color: #e2e8f0;
	border-radius: 14px;
	color: #464667;
	font-size: 11.5px;
}
.ht-filter--active {
	border-color: #20639b;
	background: #20639b;
	color: #fff;
}

.ht-cards {
	margin-bottom: 14px;
	padding: 10px 12px;
	border: 1px solid #e2e8f0;
	border-left: 4px solid #4a7eff;
	border-radius: 10px;
}
.ht-cards h3 {
	margin: 0;
	font: 700 13px "Montserrat", sans-serif;
	color: #1a4164;
}
.ht-cards__what {
	margin: 2px 0 8px;
	font-size: 12px;
	color: #72738e;
}
.ht-cards ol {
	margin: 0;
	padding: 0;
	list-style: none;
}
.ht-cards li {
	display: grid;
	grid-template-columns: 18px minmax(96px, 1.2fr) 1fr 28px;
	gap: 8px;
	align-items: center;
	padding: 3px 0;
	font-size: 12.5px;
	color: #464667;
}
.ht-cards__rank {
	font: 700 12px "Montserrat", sans-serif;
	color: #72738e;
}
.ht-cards__bar {
	height: 6px;
	border-radius: 3px;
	background: #eef2f6;
	overflow: hidden;
}
.ht-cards__bar i {
	display: block;
	height: 100%;
	border-radius: 3px;
	background: #4a7eff;
}
.ht-cards li b {
	text-align: right;
	font: 700 12.5px "Montserrat", sans-serif;
	color: #1a4164;
	font-variant-numeric: tabular-nums;
}
.ht-day h3 {
	position: sticky;
	top: 0;
	z-index: 1;
	margin: 0;
	padding: 6px 0;
	background: #fff;
	font: 700 12.5px "Montserrat", sans-serif;
	color: #1a4164;
}
.ht-list {
	margin: 0 0 12px;
	padding: 0;
	list-style: none;
}
.ht-row {
	display: grid;
	grid-template-columns: 52px 1fr;
	gap: 10px;
	padding: 8px 0 8px 10px;
	border-left: 3px solid var(--area);
	border-bottom: 1px solid #eef2f6;
}
.ht-row time {
	font-size: 12px;
	color: #72738e;
	font-variant-numeric: tabular-nums;
}
.ht-body {
	min-width: 0;
}
.ht-what {
	margin: 0;
	font-size: 13px;
	line-height: 1.4;
	color: #333333;
}
.ht-what strong {
	font-weight: 700;
	color: #1a4164;
	word-break: break-word;
}
.ht-repeat {
	margin-left: 6px;
	padding: 0 6px;
	border-radius: 8px;
	background: #eef2f6;
	font: 700 11px/1.6 "Montserrat", sans-serif;
	color: #464667;
}
.ht-tags {
	display: flex;
	flex-wrap: wrap;
	gap: 4px;
	margin: 4px 0 0;
}
.ht-tag {
	padding: 1px 8px;
	border-radius: 10px;
	background: #e8f0f8;
	color: #20639b;
	font: 600 10.5px/1.7 "Montserrat", sans-serif;
}
.ht-tag--none {
	background: #f1f1f1;
	color: #72738e;
}
.ht-tag--pending {
	background: #fff8e6;
	color: #a86f00;
}
.ht-points {
	display: flex;
	flex-wrap: wrap;
	gap: 4px;
	margin: 4px 0 0;
}
.ht-point {
	padding: 1px 8px;
	border-radius: 10px;
	background: #e7f5e7;
	color: #1f6b1d;
	font: 600 10.5px/1.7 "Montserrat", sans-serif;
	font-variant-numeric: tabular-nums;
}
.ht-point--none {
	background: #f1f1f1;
	color: #72738e;
}
</style>
